import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import {
  Datagrid,
  FunctionField,
  List,
  RefreshButton,
  TextField,
  TopToolbar,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { Iconify } from '../../../../../components/iconify';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import FilterGroup from '../../../components/FilterGroup';
import XTextInput from '../../../components/XTextInput';
import { Resources } from '../../resource';
import {
  deleteCoinImage,
  importCoinImageFromUrl,
  resetCoinImageBySymbol,
  type CoinImageExtension,
} from '../../../services/coinImagesService';
import CoinImageUploadDialog from './UploadDialog';

function formatSize(bytes?: number) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileUrl(record: Record<string, unknown>): string | undefined {
  return (record.cdnURL || record.cdnUrl || record.publicURL || record.publicUrl) as
    | string
    | undefined;
}

function previewUrl(record: Record<string, unknown>): string | undefined {
  const url = fileUrl(record);
  if (url) return url;
  const prefix = record.importSourceUrl as string | undefined;
  if (prefix && record.uploaded === false) return prefix;
  return undefined;
}

function CopyUrlButton({ url }: { url?: string }) {
  const t = useTranslate();
  const notify = useNotify();
  if (!url) return null;
  return (
    <Tooltip title={t('module.coinImages.copyLink')}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(url);
          notify(t('module.coinImages.copySuccess'), { type: 'info' });
        }}
      >
        <Iconify icon="eva:copy-outline" width={18} />
      </IconButton>
    </Tooltip>
  );
}

function CoinImageRowActions({ record }: { record: Record<string, unknown> }) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fileId = record?.id as string | undefined;
  const isLookup = Boolean(record?._lookupMode);
  const uploaded = record?.uploaded !== false && Boolean(fileUrl(record));
  const canDelete = uploaded && fileId && !String(fileId).includes('__');

  const onImport = async () => {
    const sourceUrl = String(record?.importSourceUrl ?? '').trim();
    if (!sourceUrl) return;
    setLoading(true);
    try {
      await importCoinImageFromUrl({
        sourceUrl,
        symbol: String(record?.symbol ?? '').trim() || undefined,
        extension: record?.extension as CoinImageExtension | undefined,
      });
      notify(t('module.coinImages.importSuccess'), { type: 'success' });
      refresh();
    } catch (e: any) {
      const msg =
        e?.body?.message || e?.response?.data?.message || e?.message || t('module.coinImages.importError');
      notify(msg, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    if (!fileId) return;
    setLoading(true);
    try {
      await deleteCoinImage(fileId);
      notify(t('module.coinImages.deleteSuccess'), { type: 'success' });
      setConfirmDelete(false);
      refresh();
    } catch (e: any) {
      const msg =
        e?.body?.message || e?.response?.data?.message || e?.message || t('module.coinImages.deleteError');
      notify(msg, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {/*{isLookup && !uploaded && (*/}
      {/*  <PermissionGuard resource="cdn-coin-images" action="create" fallback={<></>} loadingFallback={<></>}>*/}
      {/*    <Button size="small" variant="outlined" disabled={loading} onClick={onImport}>*/}
      {/*      {t('module.coinImages.import')}*/}
      {/*    </Button>*/}
      {/*  </PermissionGuard>*/}
      {/*)}*/}
      {canDelete && (
        <PermissionGuard resource="cdn-coin-images" action="delete" fallback={<></>} loadingFallback={<></>}>
          <Button size="small" color="error" variant="outlined" disabled={loading} onClick={() => setConfirmDelete(true)}>
            {t('actions.delete')}
          </Button>
        </PermissionGuard>
      )}
      <Dialog open={confirmDelete} onClose={() => !loading && setConfirmDelete(false)}>
        <DialogTitle>{t('module.coinImages.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('module.coinImages.deleteConfirmBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={loading}>
            {t('actions.cancel')}
          </Button>
          <PermissionGuard resource="cdn-coin-images" action="delete" fallback={<></>} loadingFallback={<></>}>
            <Button color="error" variant="contained" disabled={loading} onClick={onDelete}>
              {t('actions.delete')}
            </Button>
          </PermissionGuard>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function ResetSymbolToolbarButton() {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { filterValues } = useListContext();
  const [loading, setLoading] = useState(false);

  const symbol = String(filterValues?.symbol ?? '').trim();
  if (!symbol) return null;

  const onReset = async () => {
    setLoading(true);
    try {
      await resetCoinImageBySymbol(symbol);
      notify(t('module.coinImages.resetSuccess'), { type: 'success' });
      refresh();
    } catch (e: any) {
      const msg =
        e?.body?.message || e?.response?.data?.message || e?.message || t('module.coinImages.resetError');
      notify(msg, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PermissionGuard resource="cdn-coin-images" action="update" fallback={<></>} loadingFallback={<></>}>
      <Button size="small" variant="outlined" disabled={loading} onClick={onReset}>
        {t('module.coinImages.resetCache')}
      </Button>
    </PermissionGuard>
  );
}

function ListActions({ onUpload }: { onUpload: () => void }) {
  const t = useTranslate();
  return (
    <TopToolbar>
      <RefreshButton />
      {/*<ResetSymbolToolbarButton />*/}
      <PermissionGuard resource="cdn-coin-images" action="create" fallback={<></>} loadingFallback={<></>}>
        <Button size="small" variant="contained" onClick={onUpload} startIcon={<Iconify icon="eva:cloud-upload-fill" />}>
          {t('module.coinImages.uploadFile')}
        </Button>
      </PermissionGuard>
    </TopToolbar>
  );
}

export default function CoinImagesList() {
  const t = useTranslate();
  const [uploadOpen, setUploadOpen] = useState(false);

  const filters = useMemo(
    () => [
      <FilterGroup key="coin-image-filters" alwaysOn spacing={2} p={{ xs: 0, sm: 1 }} pt={2}>
        <XTextInput
          key="symbol"
          source="symbol"
          label={t('module.coinImages.symbol')}
          alwaysOn
          sx={{ marginBottom: 0, minWidth: 160 }}
        />
        <XTextInput
          key="filename"
          source="filename"
          label={t('module.coinImages.filename')}
          alwaysOn
          sx={{ marginBottom: 0, minWidth: 200 }}
        />
      </FilterGroup>,
    ],
    [t]
  );

  return (
    <PermissionGuard resource="cdn-coin-images" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.coinImages.title')}
          links={[
            { name: t('resource.publishing'), href: '#' },
            { name: t('module.coinImages.title') },
          ]}
        />

        <Card variant="outlined" sx={{ mt: 2, p: 0 }}>
          <Box sx={{ px: 2, pt: 2, pb: 0, typography: 'body2', color: 'text.secondary' }}>
            {t('module.coinImages.listHint')}
          </Box>
          <List
            resource={Resources.coinImages}
            exporter={false}
            perPage={50}
            empty={false}
            filters={filters as any}
            actions={<ListActions onUpload={() => setUploadOpen(true)} />}
            sort={{ field: 'uploadedAt', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid bulkActionButtons={false} rowClick={false}>
                <TextField source="symbol" label={t('module.coinImages.symbol')} emptyText="—" />
                <FunctionField
                  label={t('module.coinImages.extension')}
                  render={(r: any) => {
                    if (r?.extension) return String(r.extension).toUpperCase();
                    const name = String(r?.filename ?? '');
                    const ext = name.split('.').pop();
                    return ext ? ext.toUpperCase() : '—';
                  }}
                />
                <TextField source="filename" label={t('module.coinImages.filename')} />
                <FunctionField
                  label={t('module.coinImages.uploaded')}
                  render={(r: any) => {
                    if (r?._lookupMode) {
                      return r?.uploaded ? t('module.coinImages.yes') : t('module.coinImages.no');
                    }
                    return t('module.coinImages.yes');
                  }}
                />
                <FunctionField
                  label={t('module.coinImages.preview')}
                  render={(r: any) => {
                    const url = previewUrl(r);
                    if (!url) return '—';
                    return (
                      <Box
                        component="img"
                        src={url}
                        alt={r?.filename}
                        sx={{ width: 32, height: 32, objectFit: 'contain' }}
                      />
                    );
                  }}
                />
                <FunctionField
                  label={t('module.coinImages.size')}
                  render={(r: any) => formatSize(r?.size)}
                />
                <FunctionField
                  label={t('module.coinImages.cdnUrl')}
                  render={(r: any) => {
                    const url = fileUrl(r);
                    if (!url) return '—';
                    return (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="caption" sx={{ maxWidth: 200 }} noWrap title={url}>
                          {url}
                        </Typography>
                        <CopyUrlButton url={url} />
                        <Tooltip title={t('module.coinImages.openLink')}>
                          <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
                            <Iconify icon="eva:external-link-outline" width={18} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    );
                  }}
                />
                <FunctionField
                  label={t('module.coinImages.uploadedAt')}
                  render={(r: any) =>
                    r?.uploadedAt ? dayjs(r.uploadedAt).format('YYYY-MM-DD HH:mm:ss') : '—'
                  }
                />
                <FunctionField
                  label={t('module.coinImages.actions')}
                  render={(r: any) => <CoinImageRowActions record={r} />}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <CoinImageUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
      </DashboardContent>
    </PermissionGuard>
  );
}
